import { supabase } from '../supabase';
import { fetchAll } from './fetchAll';
import type { ProductViewRow, VisitRow } from '../storeStats';
import type { UserActivity } from '../activity';

/**
 * Räknar ett besök i en säljares butik. Inget sparas om besökaren; databasen
 * lägger till 1 på butikens summa för dagen. Fel ignoreras: statistiken får
 * aldrig störa köpet.
 */
export const recordStoreVisit = async (slug: string): Promise<void> => {
    try {
        await supabase.rpc('record_store_visit', { slug_input: slug });
    } catch {
        /* statistik är aldrig kritisk */
    }
};

/** Admin: besöken från och med en dag ("2026-09-07"). */
export const getStoreVisits = async (fromDay: string): Promise<VisitRow[]> => {
    // En rad per butik och dag: blir snabbt fler än de 1 000 som ryms i ett anrop.
    const data = await fetchAll<any>((from, to) => supabase
        .from('store_visits')
        .select('day, class_id, student_id, views')
        .gte('day', fromDay)
        .order('day').order('class_id').order('student_id')
        .range(from, to));
    return data.map(r => ({ day: r.day, classId: r.class_id, studentId: r.student_id, views: Number(r.views) }));
};

/**
 * Räknar en visning av en produktsida ('view') eller ett tillägg i varukorgen
 * ('cart') i en säljares butik. Inget sparas om besökaren. Fel ignoreras.
 */
export const recordProductEvent = async (slug: string, productId: string, event: 'view' | 'cart'): Promise<void> => {
    try {
        await supabase.rpc('record_product_event', { slug_input: slug, product_input: productId, event_input: event });
    } catch {
        /* statistik är aldrig kritisk */
    }
};

/** Admin: produktvisningarna från och med en dag ("2026-09-07"). */
export const getProductViews = async (fromDay: string): Promise<ProductViewRow[]> => {
    const data = await fetchAll<any>((from, to) => supabase
        .from('product_views')
        .select('day, product_id, class_id, views, cart_adds')
        .gte('day', fromDay)
        .order('day').order('product_id').order('class_id')
        .range(from, to));
    return data.map(r => ({ day: r.day, productId: r.product_id, classId: r.class_id, views: Number(r.views), cartAdds: Number(r.cart_adds) }));
};

/** Admin: senaste inloggning och aktivitet per person (id → tider). */
export const getUserActivity = async (): Promise<Map<string, UserActivity>> => {
    const { data, error } = await supabase.rpc('admin_user_activity');
    if (error) throw error;
    const ms = (v: string | null) => (v ? Date.parse(v) : null);
    return new Map((data || []).map((r: any) => [r.id, { lastSignInAt: ms(r.last_sign_in_at), lastSeenAt: ms(r.last_seen_at) }]));
};
